import React, { Fragment, useMemo, useState } from 'react';
import { Breadcrumbs } from '../../../../AbstractElements';
import { Container, Row, Col, Card, CardHeader, CardBody, Nav, NavItem, NavLink, TabContent, TabPane, Button, Badge } from 'reactstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useGetStoreByIdQuery, useGetStoreInventoryQuery } from '../../../../store/services/inventoryApi';
import { MapPin, User, Archive, Edit, Phone, Mail } from 'react-feather';
import DataTable from 'react-data-table-component';

const StoreInventory = ({ storeId }) => {
    const { data: inventoryData, isLoading } = useGetStoreInventoryQuery(storeId);

    const items = useMemo(() => {
        if (!inventoryData) return [];
        return Array.isArray(inventoryData) ? inventoryData : (inventoryData.data || []);
    }, [inventoryData]);

    const columns = [
        {
            name: 'Product Name',
            selector: row => row.productName,
            sortable: true,
            grow: 2,
        },
        {
            name: 'SKU',
            selector: row => row.sku,
            sortable: true,
        },
        {
            name: 'Quantity',
            selector: row => row.quantity,
            sortable: true,
            cell: row => (
                <span className={row.quantity < 10 ? 'text-danger fw-bold' : ''}>
                    {row.quantity}
                </span>
            )
        },
        {
            name: 'Avg Cost',
            selector: row => row.averageCost,
            sortable: true,
            format: row => `$${(row.averageCost || 0).toFixed(2)}`
        },
        {
            name: 'Total Value',
            selector: row => (row.quantity * (row.averageCost || 0)),
            sortable: true,
            format: row => `$${(row.quantity * (row.averageCost || 0)).toFixed(2)}`
        }
    ];

    return (
        <DataTable
            columns={columns}
            data={items}
            pagination
            progressPending={isLoading}
            noDataComponent={<div className="p-4 text-center text-muted">No inventory found in this store</div>}
            highlightOnHover
            striped
        />
    );
};

const StoreDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('1');
    const { data: storeResponse, isLoading } = useGetStoreByIdQuery(id);
    const store = storeResponse?.data || storeResponse;

    if (isLoading) return <div className="p-5 text-center">Loading Store Details...</div>;
    if (!store) return <div className="p-5 text-center text-danger">Store not found</div>;

    return (
        <Fragment>
            <Breadcrumbs parent="Inventory" title="Store Details" mainTitle={store.storeName} />
            <Container fluid={true}>
                <Row>
                    <Col xl="4" lg="5">
                        <Card>
                            <CardHeader className="d-flex justify-content-between align-items-center">
                                <h5>Profile</h5>
                                <Button color="primary" size="xs" onClick={() => navigate(`/inventory/stores/edit/${id}`)}>
                                    <Edit size={14} className="me-1" /> Edit
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <div className="text-center mb-4">
                                    <div className="bg-light p-4 rounded-circle d-inline-block mb-3">
                                        <Archive size={40} className="text-primary" />
                                    </div>
                                    <h4>{store.storeName}</h4>
                                    <p className="text-muted">{store.storeNumber || 'N/A'}</p>
                                    <div className="d-flex justify-content-center gap-2">
                                        <Badge color={store.status === 'active' ? 'success' : 'danger'}>
                                            {store.status === 'active' ? 'Active' : 'Inactive'}
                                        </Badge>
                                    </div>
                                </div>
                                <div className="contact-info">
                                    <div className="d-flex align-items-center mb-3">
                                        <User size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Manager</p>
                                            <p className="mb-0">{store.storeManager || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center mb-3">
                                        <Mail size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Email</p>
                                            <p className="mb-0">{store.email || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center mb-3">
                                        <Phone size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Contact Number</p>
                                            <p className="mb-0">{store.phone || 'N/A'}</p>
                                        </div>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <MapPin size={18} className="me-3 text-primary" />
                                        <div>
                                            <p className="mb-0 font-weight-bold">Address</p>
                                            <p className="mb-0">{store.storeAddress}</p>
                                        </div>
                                    </div>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col xl="8" lg="7">
                        <Card>
                            <CardHeader>
                                <Nav tabs>
                                    <NavItem>
                                        <NavLink className={activeTab === '1' ? 'active' : ''} onClick={() => setActiveTab('1')}>
                                            Current Inventory
                                        </NavLink>
                                    </NavItem>
                                </Nav>
                            </CardHeader>
                            <CardBody>
                                <TabContent activeTab={activeTab}>
                                    <TabPane tabId="1">
                                        <StoreInventory storeId={id} />
                                    </TabPane>
                                </TabContent>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default StoreDetails;
